import { useAtom } from '@xstate/store/react';

import type { AudioFeatureType } from '../../api/cc-smarts/models/AudioFeature';
import type { ActiveContent, ContentDetails } from '../../types/activeContent';
import type { AudioContainerMode } from '../../types/audioContainerMode';
import {
	activatePendingContent,
	audioPlayerState,
	clearPendingContent,
	closeAudioPlayer,
	getCustomization,
	getSupportingContent,
	hideAudioPlayer,
	openAudioPlayer,
	resetPlayImmediately,
	resetPlaylistDefaultExpanded,
	setActiveAudioContainerMode,
	setActiveFeature,
	setActiveMenu,
	setCustomization,
	setHasOpenDropdownMenu,
	setIsPaused,
	setIsPlaying,
	setPlaybackSpeed,
	setPlaylistHasUnavailableContent,
	setSupportingContent,
	updatePlayerState,
	type AudioPlayerState,
} from '../state/AudioPlayerState';
import type {
	ConfluenceTone,
	ConfluenceLength,
} from '../ui/audio-player/__generated__/AudioContainerQuery.graphql';
import type { AudioFeatureMenuItem } from '../ui/audio-player/menu/AudioFeatureMenuGroup';

export const useAudioPlayerStore = (): {
	// State
	isOpen: boolean;
	isPlaying: boolean;
	isHidden: boolean;
	isPaused: boolean;
	activeContent: ActiveContent;
	activeFeature: AudioFeatureType;
	activeMenu: AudioFeatureMenuItem | undefined;
	activeAudioContainerMode: AudioContainerMode;
	pendingContent: ActiveContent | null;
	cloudId: string;
	customization: {
		tone: ConfluenceTone;
		length: ConfluenceLength;
		playbackSpeed: number;
	};
	playlistDefaultExpanded: boolean | undefined;
	playImmediately: boolean | undefined;
	playlistHasUnavailableContent: boolean;
	hasOpenDropdownMenu: boolean;
	// Actions
	openAudioPlayer: (
		contentToOpen: ActiveContent,
		options?: {
			isUninitialized?: boolean;
			playImmediately?: boolean;
			expandPlaylist?: boolean;
			returnFocusRef?: React.RefObject<HTMLElement>;
		},
	) => void;
	closeAudioPlayer: () => void;
	hideAudioPlayer: () => void;
	activatePendingContent: () => void;
	clearPendingContent: () => void;
	setIsPlaying: (isPlaying: boolean) => void;
	setIsPaused: (isPaused: boolean) => void;
	updatePlayerState: (updates: Partial<AudioPlayerState>) => void;
	setActiveFeature: (activeFeature: AudioFeatureType) => void;
	setActiveMenu: (activeMenu: AudioFeatureMenuItem | undefined) => void;
	setActiveAudioContainerMode: (activeAudioContainerMode: AudioContainerMode) => void;
	setCustomization: (newCustomization: AudioPlayerState['customization']) => void;
	getCustomization: () => {
		tone: ConfluenceTone;
		length: ConfluenceLength;
		playbackSpeed: number;
	};
	setPlaybackSpeed: (playbackSpeed: number) => void;
	getSupportingContent: () => ContentDetails[];
	setSupportingContent: (supportingContent: ActiveContent['supportingContent']) => void;
	resetPlaylistDefaultExpanded: () => void;
	resetPlayImmediately: () => void;
	setPlaylistHasUnavailableContent: (playlistHasUnavailableContent: boolean) => void;
	setHasOpenDropdownMenu: (hasOpenDropdownMenu: boolean) => void;
} => {
	const state = useAtom(audioPlayerState);

	return {
		// State
		isOpen: state.isOpen,
		isPlaying: state.isPlaying,
		isHidden: state.isHidden,
		isPaused: state.isPaused,
		activeContent: state.activeContent,
		activeFeature: state.activeFeature,
		activeMenu: state.activeMenu,
		activeAudioContainerMode: state.activeAudioContainerMode,
		pendingContent: state.pendingContent,
		cloudId: state.cloudId,
		customization: state.customization,
		playlistDefaultExpanded: state.playlistDefaultExpanded,
		playImmediately: state.playImmediately,
		playlistHasUnavailableContent: state.playlistHasUnavailableContent,
		hasOpenDropdownMenu: state.hasOpenDropdownMenu,

		// Actions
		openAudioPlayer,
		closeAudioPlayer,
		hideAudioPlayer,
		activatePendingContent,
		clearPendingContent,
		setIsPlaying,
		setIsPaused,
		updatePlayerState,
		setActiveFeature,
		setActiveMenu,
		setActiveAudioContainerMode,
		setCustomization,
		getCustomization,
		setPlaybackSpeed,
		getSupportingContent,
		setSupportingContent,
		resetPlaylistDefaultExpanded,
		resetPlayImmediately,
		setPlaylistHasUnavailableContent,
		setHasOpenDropdownMenu,
	};
};
